/**
 * ============================================================================
 * GRATIA - assets/product.js
 * ----------------------------------------------------------------------------
 * Product page Web Components:
 *   <variant-selects>  - resolve variant client-side, update price / stock /
 *                        media / URL (no reload)
 *   <product-gallery>  - thumbnails + prev/next + variant-driven media switch
 *   <quantity-input>   - product-form stepper (cart steppers live in cart.js)
 *   <share-button>     - Web Share API with copy-link fallback
 * Plus recently-viewed tracking (localStorage).
 * ============================================================================
 */
(function () {
  'use strict';
  const Gratia = (window.Gratia = window.Gratia || {});
  const strings = Gratia.strings || {};

  /* ---- Shopify money formatter --------------------------------------------- */
  function formatMoney(cents, format) {
    const fmt = format || Gratia.cart?.moneyFormat || '${{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withCommas(number) {
      return number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    }
    function fmtNum(num, precision, thousands, decimal) {
      precision = precision ?? 2;
      thousands = thousands ?? ',';
      decimal = decimal ?? '.';
      const value = (num / 100).toFixed(precision);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
    let value = '';
    const match = fmt.match(placeholder);
    switch (match && match[1]) {
      case 'amount': value = fmtNum(cents, 2, ',', '.'); break;
      case 'amount_no_decimals': value = fmtNum(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = fmtNum(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmtNum(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmtNum(cents, 2, "'", '.'); break;
      default: value = fmtNum(cents, 2, ',', '.');
    }
    return fmt.replace(placeholder, value);
  }
  Gratia.money = formatMoney;

  /* ======================================================================= */
  /* <product-gallery>                                                        */
  /* ======================================================================= */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.thumbs.forEach((t) =>
        t.addEventListener('click', () => this.setActive(t.dataset.mediaId))
      );
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
    }
    get activeIndex() {
      return this.slides.findIndex((s) => !s.classList.contains('hidden'));
    }
    step(dir) {
      const next = (this.activeIndex + dir + this.slides.length) % this.slides.length;
      this.setActive(this.slides[next].dataset.mediaId);
    }
    setActive(mediaId) {
      if (!mediaId) return;
      this.slides.forEach((s) => s.classList.toggle('hidden', s.dataset.mediaId !== String(mediaId)));
      this.thumbs.forEach((t) => {
        const active = t.dataset.mediaId === String(mediaId);
        t.classList.toggle('border-primary', active);
        t.classList.toggle('border-transparent', !active);
        active ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current');
      });
      // pause any playing media in hidden slides
      this.querySelectorAll('video').forEach((v) => { if (v.closest('.hidden')) v.pause?.(); });
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ======================================================================= */
  /* <quantity-input> (product form only)                                     */
  /* ======================================================================= */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-qty-input]');
      if (!this.input || this.input.dataset.lineKey) return; // cart lines handled by cart.js
      this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.change(-1));
      this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.change(1));
    }
    change(dir) {
      const min = parseInt(this.input.min, 10) || 1;
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      let v = (parseInt(this.input.value, 10) || min) + dir;
      v = Math.min(max, Math.max(min, v));
      this.input.value = v;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ======================================================================= */
  /* <variant-selects>                                                        */
  /* ======================================================================= */
  class VariantSelects extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-variant-data]');
      if (!dataEl) return;
      try { this.variants = JSON.parse(dataEl.textContent); } catch { this.variants = []; }

      // Server-rendered stock map (see snippets/product-variant-picker.liquid).
      // Shopify's variant JSON often reports inventory_quantity as 0, which
      // would make every variant look empty client-side, so merge the trusted
      // figures in before anything reads them.
      const invEl = this.querySelector('[data-variant-inventory]');
      if (invEl) {
        let inv = null;
        try { inv = JSON.parse(invEl.textContent); } catch { inv = null; }
        if (inv) {
          this.variants.forEach((v) => {
            const row = inv[v.id];
            if (!row) return;
            v.inventory_quantity = row.qty;
            v.inventory_management = row.tracked ? 'shopify' : null;
            v.inventory_policy = row.policy;
            v.available = row.available;
          });
        }
      }
      this.section = this.closest('[data-product-section]');
      this.addEventListener('change', () => this.onChange());
      this.onChange(true);
    }

    selectedOptions() {
      const opts = [];
      this.querySelectorAll('[data-option-value]:checked').forEach((input) => {
        opts[parseInt(input.dataset.optionPosition, 10) - 1] = input.value;
      });
      return opts;
    }

    onChange(initial) {
      const selected = this.selectedOptions();
      // reflect chosen value in the legend
      this.querySelectorAll('.variant-option').forEach((fs) => {
        const checked = fs.querySelector('[data-option-value]:checked');
        const label = fs.querySelector('[data-option-selected-value]');
        if (checked && label) label.textContent = checked.value;
      });

      const variant = this.variants.find((v) =>
        v.options.every((opt, i) => opt === selected[i])
      );
      this.currentVariant = variant;
      if (initial && variant) return; // don't disturb server-rendered state on load
      this.update(variant);
    }

    update(variant) {
      const root = this.section || document;
      const idInput = root.querySelector('[data-variant-id-input]');
      const addBtn = root.querySelector('[data-add-button]');
      const addText = root.querySelector('[data-add-button-text]');
      const priceWrap = root.querySelector('[data-product-price]');
      const skuEl = root.querySelector('[data-product-sku]');
      const gallery = root.querySelector('[data-product-gallery]');

      // No variant matches the chosen options (an incomplete option matrix,
      // e.g. Large exists and Blue exists but Large/Blue does not). Everything
      // downstream must still be told, or the panel keeps the PREVIOUS
      // variant's price, SKU, stock pill, quantity ceiling and hidden id, and
      // the quote dialog would email a request naming the wrong variant.
      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addText) addText.textContent = strings.unavailable || 'Unavailable';
        if (idInput) idInput.disabled = true;
        if (skuEl) skuEl.textContent = '';
        // Blank the price too, or the customer reads a concrete amount for a
        // combination that does not exist. Replace the CONTENTS and keep the
        // .price node itself: the recovery path below looks it up by class to
        // restore a real price when a valid variant is chosen again.
        const priceInner = priceWrap && priceWrap.querySelector('.price');
        if (priceInner) {
          priceInner.innerHTML =
            '<span class="price__regular text-muted">' +
            (strings.unavailable || 'Unavailable') +
            '</span>';
        }
        const wrap = root.querySelector('[data-product-inventory]');
        const textEl = wrap && wrap.querySelector('[data-inventory-text]');
        const dot = wrap && wrap.querySelector('span.rounded-full');
        if (dot) dot.className = 'inline-flex h-2 w-2 rounded-full bg-error';
        if (textEl) {
          textEl.textContent = strings.unavailable || 'Unavailable';
          textEl.className = 'font-medium text-error';
        }
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: null } }));
        return;
      }

      // URL + hidden id
      if (idInput) { idInput.value = variant.id; idInput.disabled = !variant.available; }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // price
      if (priceWrap) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        let html = '<div class="price ' + (onSale ? 'price--on-sale' : '') + '">';
        if (onSale) {
          html += '<span class="price__sale">' + formatMoney(variant.price) + '</span>';
          html += '<s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s>';
        } else {
          html += '<span class="price__regular">' + formatMoney(variant.price) + '</span>';
        }
        html += '</div>';
        const inner = priceWrap.querySelector('.price');
        if (inner) inner.outerHTML = html;
      }

      // add button. A sold-out variant on a product that offers the order
      // request keeps a disabled button in the DOM, but theme.js hides it and
      // reveals the request CTA instead, so the customer is never left with a
      // dead "Sold out" and no way forward.
      if (addBtn) addBtn.disabled = !variant.available;
      if (addText) addText.textContent = variant.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');

      // sku
      if (skuEl) {
        skuEl.textContent = variant.sku || '';
        skuEl.closest('[data-product-info] p, .main-product__info p')?.classList.toggle('hidden', !variant.sku);
      }

      // inventory
      this.updateInventory(root, variant);
      this.updateQuantityMax(root, variant);

      // media
      if (gallery && typeof gallery.setActive === 'function' && variant.featured_media) {
        gallery.setActive(variant.featured_media.id);
      }

      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }

    /**
     * Liquid renders the quantity ceiling for the FIRST variant only, so every
     * other variant would inherit it (letting a customer order 10 of a
     * 1-in-stock variant). Recompute it on each switch. When the quote CTA is
     * on the field stays uncapped on purpose: exceeding stock is what swaps
     * add-to-cart for the request button.
     */
    updateQuantityMax(root, variant) {
      const input = root.querySelector('[data-qty-input]');
      if (!input) return;
      const quoteOn = !!input.closest('[data-quote-form]');
      // Same rule as the quote ceiling: tracked and not overselling.
      const limited =
        variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue';

      if (quoteOn || !limited) {
        input.removeAttribute('max');
        return;
      }
      const max = Math.max(1, variant.inventory_quantity);
      input.max = max;
      if ((parseInt(input.value, 10) || 1) > max) {
        input.value = max;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateInventory(root, variant) {
      const wrap = root.querySelector('[data-product-inventory]');
      if (!wrap) return;
      const textEl = wrap.querySelector('[data-inventory-text]');
      const dot = wrap.querySelector('span.rounded-full');
      const threshold = parseInt(wrap.dataset.threshold, 10) || 10;
      const tracked = wrap.dataset.tracked === 'true';
      const setDot = (c) => dot && (dot.className = 'inline-flex h-2 w-2 rounded-full ' + c);
      if (!variant.available) {
        setDot('bg-error');
        if (textEl) { textEl.textContent = strings.outOfStock || 'Out of stock'; textEl.className = 'font-medium text-error'; }
        // Mirror the Liquid conditions in snippets/product-inventory.liquid:
        // the unit count is only meaningful when the variant tracks stock AND
        // refuses to oversell. Without the policy check a variant set to keep
        // selling would claim "only N left" and never update past it.
      } else if (
        tracked &&
        variant.inventory_management === 'shopify' &&
        variant.inventory_policy === 'deny' &&
        variant.inventory_quantity <= threshold &&
        variant.inventory_quantity > 0
      ) {
        setDot('bg-warning');
        if (textEl) { textEl.textContent = (strings.lowStock || 'Only [count] left').replace('[count]', variant.inventory_quantity); textEl.className = 'font-medium text-warning'; }
      } else {
        setDot('bg-success');
        if (textEl) { textEl.textContent = strings.inStock || 'In stock'; textEl.className = 'font-medium text-success'; }
      }
    }
  }
  if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);

  /* ======================================================================= */
  /* <share-button>                                                           */
  /* ======================================================================= */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-share-trigger]');
      this.success = this.querySelector('[data-share-success]');
      this.url = this.dataset.shareUrl;
      this.title = this.dataset.shareTitle;
      this.trigger?.addEventListener('click', () => this.share());
    }
    async share() {
      if (navigator.share) {
        try { await navigator.share({ title: this.title, url: this.url }); } catch { /* cancelled */ }
        return;
      }
      try {
        await navigator.clipboard.writeText(this.url);
        this.success?.classList.remove('hidden');
        setTimeout(() => this.success?.classList.add('hidden'), 2500);
      } catch { window.prompt('Copy link', this.url); }
    }
  }
  if (!customElements.get('share-button')) customElements.define('share-button', ShareButton);

  /* ======================================================================= */
  /* Recently viewed (localStorage)                                           */
  /* ======================================================================= */
  try {
    const pj = document.querySelector('[data-product-json]');
    if (pj) {
      const data = JSON.parse(pj.textContent);
      const KEY = 'gratia:recently-viewed';
      let list = JSON.parse(localStorage.getItem(KEY) || '[]');
      list = list.filter((h) => h !== data.handle);
      list.unshift(data.handle);
      list = list.slice(0, 12);
      localStorage.setItem(KEY, JSON.stringify(list));
    }
  } catch { /* ignore */ }
})();
